/**
 * The stimulator abstraction.
 *
 * A `Stimulator` is an external actuator that MAY be a device delivering
 * electrical stimulation to a person. Everything in this file is written on
 * that assumption, including the parts that look like over-engineering.
 *
 * Three rules hold for every implementation:
 *
 *   1. A stimulator starts DISCONNECTED and DISARMED. Running the game is not
 *      consent to stimulate; only an explicit operator action is.
 *   2. `requestStimulus` decides *when* a stimulation happens and for how long,
 *      within operator-set bounds. It can never decide *how strong* it is.
 *      There is no intensity, amplitude, frequency, or waveform field in
 *      `StimulusRequest`, deliberately -- see the note below.
 *   3. A successful `StimulusResult` means "the stimulator says it delivered a
 *      pulse". It does NOT mean the participant moved, and it must never be
 *      turned into a game input. Only a detected `InputDevice` event may do
 *      that.
 *
 * ## Why there is no intensity parameter
 *
 * Stimulation intensity, electrode placement, and waveform are the parameters
 * that determine whether stimulation is comfortable, effective, or harmful.
 * They are set by a human operator, on the hardware, following the
 * manufacturer's documented procedure, and verified on the participant before a
 * session starts. Exposing them to an autonomous controller would let a bot
 * optimise a physiological dose against a game score. That is not a feature
 * this software will have, so the field does not exist to be misused.
 */

export interface StimulusRequest {
  /** Correlation id shared by every event in this intent -> flap chain. */
  requestId: string;
  /** performance.now() when the request was formed. */
  requestedAt: number;
  /** Hardware channel index, as configured by the operator. */
  channel: number;
  /**
   * How long the stimulator should be triggered for, ms.
   * Clamped by `SafetyLimits.maxDurationMs` before it reaches any device.
   */
  durationMs: number;
  /**
   * Opaque id of an operator-configured hardware preset, if the device has
   * them. The software passes it through and never interprets it.
   */
  presetId?: string;
  /** Free-text provenance for the log, e.g. "bot: below target". */
  reason?: string;
}

export interface StimulusResult {
  requestId: string;
  accepted: boolean;
  /** Set when the safety layer or the device refused the request. */
  rejectedReason?: string;
  /** performance.now() when the device reported the pulse beginning. */
  startedAt?: number;
  /** performance.now() when the device reported the pulse ending. */
  endedAt?: number;
  error?: string;
}

export type StimulatorKind = 'mock' | 'simulated-human' | 'backyard-brains-hhi';

export interface StimulatorStatus {
  kind: StimulatorKind;
  label: string;
  /**
   * False ONLY for an adapter that can energise real electrodes. The dashboard
   * uses this to decide how loud the safety banner is.
   */
  simulated: boolean;

  connected: boolean;
  armed: boolean;
  emergencyStopped: boolean;

  requestCount: number;
  acceptedCount: number;
  rejectedCount: number;
  deliveredCount: number;

  lastRequestAt: number | null;
  lastStimulusAt: number | null;

  /**
   * Human-readable note about the intensity the OPERATOR configured on the
   * hardware. Informational only; the software neither sets nor validates it.
   */
  operatorIntensityNote?: string;

  detail?: Record<string, unknown>;
}

export interface Stimulator {
  readonly kind: StimulatorKind;
  readonly label: string;
  readonly simulated: boolean;

  connect(): Promise<void>;
  disconnect(): Promise<void>;

  arm(): Promise<void>;
  disarm(): Promise<void>;

  requestStimulus(request: StimulusRequest): Promise<StimulusResult>;

  getStatus(): StimulatorStatus;

  /**
   * Immediate, synchronous best effort to stop everything. Called by the
   * emergency stop. Must not throw, must not await, must be safe to call at any
   * time including when disconnected.
   *
   * Software emergency stop is a convenience layer. The hardware's own
   * documented emergency stop -- power switch, intensity dial, physically
   * removing electrodes -- is the mechanism that is actually relied upon.
   */
  emergencyStop(): void;

  /** Called once per frame by the app, for implementations that need a clock. */
  pump?(nowMs: number): void;
}

/** Bookkeeping every implementation needs; none of the policy. */
export abstract class BaseStimulator implements Stimulator {
  abstract readonly kind: StimulatorKind;
  abstract readonly label: string;
  abstract readonly simulated: boolean;

  protected connected = false;
  protected armed = false;
  protected stopped = false;

  protected requestCount = 0;
  protected acceptedCount = 0;
  protected rejectedCount = 0;
  protected deliveredCount = 0;
  protected lastRequestAt: number | null = null;
  protected lastStimulusAt: number | null = null;

  abstract connect(): Promise<void>;
  abstract disconnect(): Promise<void>;
  abstract requestStimulus(request: StimulusRequest): Promise<StimulusResult>;

  async arm(): Promise<void> {
    if (!this.connected) throw new Error('Cannot arm a disconnected stimulator');
    if (this.stopped) throw new Error('Emergency stop is latched; clear it before arming');
    this.armed = true;
  }

  async disarm(): Promise<void> {
    this.armed = false;
  }

  emergencyStop(): void {
    this.stopped = true;
    this.armed = false;
  }

  /** Clear a latched emergency stop. Operator action only. */
  clearEmergencyStop(): void {
    this.stopped = false;
  }

  getStatus(): StimulatorStatus {
    return {
      kind: this.kind,
      label: this.label,
      simulated: this.simulated,
      connected: this.connected,
      armed: this.armed,
      emergencyStopped: this.stopped,
      requestCount: this.requestCount,
      acceptedCount: this.acceptedCount,
      rejectedCount: this.rejectedCount,
      deliveredCount: this.deliveredCount,
      lastRequestAt: this.lastRequestAt,
      lastStimulusAt: this.lastStimulusAt,
      detail: this.detail(),
    };
  }

  protected detail(): Record<string, unknown> | undefined {
    return undefined;
  }

  protected reject(request: StimulusRequest, reason: string): StimulusResult {
    this.requestCount++;
    this.rejectedCount++;
    this.lastRequestAt = request.requestedAt;
    return { requestId: request.requestId, accepted: false, rejectedReason: reason };
  }

  /**
   * Shared preconditions. Every implementation calls this first; none of them
   * gets to decide that it is exempt.
   */
  protected guard(request: StimulusRequest): StimulusResult | null {
    if (this.stopped) return this.reject(request, 'emergency stop latched');
    if (!this.connected) return this.reject(request, 'stimulator not connected');
    if (!this.armed) return this.reject(request, 'stimulator disarmed');
    return null;
  }
}
